import {
  ApiWalletCustomer,
  ApiWalletTransaction,
  ApiWalletTransactionMetadata,
  WalletApiTransactionType,
  WalletSummary,
  WalletTransaction,
  WalletTransactionType,
  WalletTransactionsPage,
} from "./walletTypes";

/** Money and bounds arrive as strings or numbers; anything unreadable is 0. */
export const toWalletNumber = (raw: unknown): number => {
  if (typeof raw === "number") {
    return Number.isFinite(raw) ? raw : 0;
  }
  if (typeof raw === "string" && raw.trim() !== "") {
    const parsed = Number(raw.trim());

    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
};

const toOptionalNumber = (raw: unknown): number | null =>
  raw === undefined || raw === null || raw === "" ? null : toWalletNumber(raw);

export const parseWalletSummary = (raw: ApiWalletCustomer): WalletSummary => ({
  availableBalance: toWalletNumber(raw.availableBalance),
  totalBalance: toWalletNumber(raw.totalBalance),
  heldBalance: toWalletNumber(raw.heldBalance),
  currency: raw.currency,
});

// Same mapping as the app (wallet_transaction_dto.dart). Types missing here
// (HOLD / UNHOLD, or anything new) parse as UNKNOWN and are never shown.
const TRANSACTION_TYPES: Partial<Record<string, WalletTransactionType>> = {
  [WalletApiTransactionType.CAPTURE]: WalletTransactionType.PURCHASE,
  [WalletApiTransactionType.TOPUP]: WalletTransactionType.TOP_UP,
  [WalletApiTransactionType.TOP_UP]: WalletTransactionType.TOP_UP,
  [WalletApiTransactionType.REFUND]: WalletTransactionType.REFUND,
};

const parseMetadata = (
  raw: ApiWalletTransaction["metadata"]
): ApiWalletTransactionMetadata | null => {
  if (!raw) {
    return null;
  }
  if (typeof raw !== "string") {
    return raw;
  }
  try {
    const parsed: unknown = JSON.parse(raw);

    return parsed && typeof parsed === "object"
      ? (parsed as ApiWalletTransactionMetadata)
      : null;
  } catch {
    return null;
  }
};

export const parseWalletTransaction = (
  raw: ApiWalletTransaction
): WalletTransaction => {
  const metadata = parseMetadata(raw.metadata);
  const receiptId = toOptionalNumber(
    metadata?.capture?.receiptId ?? metadata?.refund?.receiptId
  );
  const topUp = metadata?.topUp;

  return {
    id: raw.transactionId,
    type:
      TRANSACTION_TYPES[raw.type?.toUpperCase()] ??
      WalletTransactionType.UNKNOWN,
    amount: toWalletNumber(raw.amount),
    currency: raw.currency,
    balanceAfter: toOptionalNumber(
      raw.balancesAfter?.availableBalance ?? raw.balanceAfter
    ),
    createdAt: raw.createdAt,
    referenceId: raw.referenceId || null,
    receiptId,
    topUp: topUp
      ? {
          topUpId: topUp.topUpId || null,
          paidAmount: toWalletNumber(topUp.paidAmount),
          cashbackAmount: toWalletNumber(topUp.cashbackAmount),
          points: toWalletNumber(topUp.points),
        }
      : null,
  };
};

export const parseWalletTransactionsPage = (raw: {
  items?: ApiWalletTransaction[];
  nextCursor?: string;
  hasMore?: boolean;
}): WalletTransactionsPage => ({
  items: (raw.items ?? [])
    .map(parseWalletTransaction)
    .filter((tx) => tx.type !== WalletTransactionType.UNKNOWN),
  nextCursor: raw.nextCursor ?? null,
  hasMore: !!raw.hasMore,
});
